/**
 * Lagerbestand mit Bewertung, Bewegungen und Inventur.
 */

import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { formatAmount, formatDate, formatPrice, formatQuantity, today } from '@erp/shared';

import { ApiError, api, withQuery } from '../api/client';
import { useAuth } from '../auth';
import { PageHeader } from '../components/Layout';
import { useToast } from '../components/toast';
import {
  Alert,
  Dialog,
  EmptyState,
  Field,
  LoadingBlock,
  NumericInput,
  Select,
  TextArea,
  TextInput,
} from '../components/ui';

type StockRow = {
  id: string;
  number: string;
  name: string;
  unit: string;
  stockQuantity: number;
  /** Fuer offene Auftraege reservierte Menge (artikelweit). */
  reservedQuantity: number;
  /** Bestand minus reserviert. */
  freeQuantity: number;
  minStock: number;
  reorderQuantity: number;
  averageCost: number;
  supplierName: string | null;
  categoryId: string | null;
  categoryName: string | null;
  /** Abgänge der letzten 12 Monate in Tausendsteln. */
  outflow: number;
  /** Umschläge je Jahr in Hundertsteln, null ohne Abgänge oder Bestand. */
  turnover: number | null;
  /** Durchschnittliche Lagerdauer in Tagen. */
  stockDays: number | null;
  /** Reichweite des heutigen Bestands in Tagen beim Absatz der letzten 12 Monate. */
  coverageDays: number | null;
};

type MovementRow = {
  id: string;
  movementDate: string;
  movementType: string;
  quantity: number;
  unitCost: number;
  balanceAfter: number;
  documentNumber: string | null;
  note: string | null;
  itemNumber: string;
  itemName: string;
};

const MOVEMENT_LABELS: Record<string, string> = {
  goods_receipt: 'Wareneingang',
  delivery: 'Lieferung',
  correction: 'Korrektur',
  inventory: 'Inventur',
  scrap: 'Ausbuchung',
  transfer_in: 'Umlagerung zu',
  transfer_out: 'Umlagerung von',
  opening_balance: 'Anfangsbestand',
  complaint_return: 'Rücklauf (Reklamation)',
  repair_consumption: 'Verbrauch (Reparatur)',
};

/** Umschläge je Jahr (Hundertstel): unter 1 mit zwei, sonst mit einer Nachkommastelle. */
function formatTurnover(hundredths: number): string {
  const ratio = hundredths / 100;
  const digits = ratio < 1 ? 2 : 1;
  return `${ratio.toLocaleString('de-DE', { minimumFractionDigits: digits, maximumFractionDigits: digits })} ×`;
}

function outflowTitle(row: StockRow): string {
  return row.outflow > 0
    ? `Abgänge der letzten 12 Monate: ${formatQuantity(row.outflow)} ${row.unit}`
    : 'Keine Abgänge in den letzten 12 Monaten';
}

/** Tage: bis zwei Jahre in Tagen, darüber in Jahren. */
function formatDays(days: number): string {
  if (days <= 730) return `${days} Tage`;
  const years = days / 365;
  return years > 99 ? 'über 99 Jahre' : `${years.toLocaleString('de-DE', { maximumFractionDigits: 1 })} Jahre`;
}

type WarehouseBalance = {
  warehouseId: string;
  code: string;
  name: string;
  quantity: number;
};

export function StockPage() {
  const { can } = useAuth();
  const [tab, setTab] = useState<'stock' | 'movements'>('stock');
  const [search, setSearch] = useState('');
  const [adjustItem, setAdjustItem] = useState<StockRow | null>(null);
  const [transferItem, setTransferItem] = useState<StockRow | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const [category, setCategory] = useState('');
  const [warehouse, setWarehouse] = useState('');

  const stock = useQuery({
    queryKey: ['stock', search, category, warehouse],
    queryFn: () =>
      api.get<{ items: StockRow[]; total: number; stockValue: number }>(
        withQuery('/api/stock', {
          q: search || undefined,
          categoryId: category || undefined,
          warehouseId: warehouse || undefined,
          limit: 200,
        }),
      ),
    placeholderData: keepPreviousData,
    enabled: tab === 'stock',
  });

  const warehouseList = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => api.get<{ warehouses: { id: string; code: string; name: string; active?: boolean }[] }>('/api/stock/warehouses'),
    enabled: tab === 'stock',
  });

  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<{ categories: { id: string; name: string }[] }>('/api/categories'),
    enabled: tab === 'stock',
  });

  const movements = useQuery({
    queryKey: ['stock-movements'],
    queryFn: () =>
      api.get<{ items: MovementRow[]; total: number }>(withQuery('/api/stock/movements', { limit: 100 })),
    enabled: tab === 'movements',
  });

  return (
    <>
      <PageHeader
        title="Lagerbestand"
        subtitle={
          stock.data
            ? `${stock.data.total} bestandsgeführte Artikel · Lagerwert ${formatAmount(stock.data.stockValue)} €`
            : undefined
        }
      />

      <div className="row" style={{ marginBottom: 16, gap: 6 }}>
        <button
          type="button"
          className={`btn btn-sm${tab === 'stock' ? ' btn-primary' : ''}`}
          onClick={() => setTab('stock')}
        >
          Bestand
        </button>
        <button
          type="button"
          className={`btn btn-sm${tab === 'movements' ? ' btn-primary' : ''}`}
          onClick={() => setTab('movements')}
        >
          Bewegungen
        </button>
      </div>

      {can('stock:write') ? <ReconcileBanner /> : null}

      {tab === 'stock' ? (
        <div className="card">
          <div className="table-toolbar" style={{ gap: 10 }}>
            <TextInput value={search} onChange={setSearch} placeholder="Artikel suchen …" />
            <div style={{ minWidth: 220 }}>
              <Select
                value={category}
                onChange={setCategory}
                options={[
                  { value: '', label: 'Alle Warengruppen' },
                  ...(categories.data?.categories ?? []).map((entry) => ({ value: entry.id, label: entry.name })),
                ]}
              />
            </div>
            <div style={{ minWidth: 200 }} title="Mit Lager zeigt die Spalte Bestand nur die Menge in diesem Lager">
              <Select
                value={warehouse}
                onChange={setWarehouse}
                options={[
                  { value: '', label: 'Alle Lager' },
                  ...(warehouseList.data?.warehouses ?? [])
                    .filter((entry) => entry.active !== false)
                    .map((entry) => ({ value: entry.id, label: `${entry.code} · ${entry.name}` })),
                ]}
              />
            </div>
          </div>

          {stock.isLoading ? <LoadingBlock /> : null}

          {stock.error ? (
            <div style={{ padding: 14 }}>
              <Alert kind="error">
                Der Bestand konnte nicht geladen werden
                {stock.error instanceof ApiError ? `: ${stock.error.message}` : '.'}
              </Alert>
            </div>
          ) : null}

          {stock.data && !stock.isLoading ? (
            stock.data.items.length === 0 ? (
              <EmptyState title={warehouse || category || search ? "Keine Artikel mit Bestand in dieser Auswahl" : "Keine bestandsgeführten Artikel"} />
            ) : (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th className="shrink">Nummer</th>
                      <th>Bezeichnung</th>
                      <th>Warengruppe</th>
                      <th>Lieferant</th>
                      <th className="numeric">Bestand</th>
                      <th className="numeric" title="Für offene Aufträge reserviert">Reserviert</th>
                      <th className="numeric" title="Bestand minus reserviert - das ist noch verfügbar">Frei</th>
                      <th className="numeric">Meldebestand</th>
                      <th className="numeric">Einstand</th>
                      <th className="numeric">Lagerwert</th>
                      <th
                        className="numeric"
                        title="Umschlagshäufigkeit je Jahr: Abgänge der letzten 12 Monate geteilt durch den durchschnittlichen Bestand"
                      >
                        Umschlag
                      </th>
                      <th className="numeric" title="Wie lange ein Stück im Schnitt liegt: 365 Tage geteilt durch den Umschlag">
                        Lagerdauer
                      </th>
                      <th className="numeric" title="Wie lange der heutige Bestand beim Absatz der letzten 12 Monate reicht">
                        Reichweite
                      </th>
                      {can('stock:write') ? <th className="shrink"></th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {stock.data.items.map((row) => {
                      const below = row.minStock > 0 && row.stockQuantity < row.minStock;
                      // Menge (Tausendstel) x Preis (Zehntausendstel) -> Cent
                      const value = Math.round((row.stockQuantity * row.averageCost) / 100_000);

                      return (
                        <Fragment key={row.id}>
                          <tr>
                            <td className="mono">
                              <Link to={`/artikel/${row.id}`}>{row.number}</Link>
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-quiet btn-sm"
                                style={{ padding: 0, fontWeight: 400 }}
                                onClick={() => setExpanded(expanded === row.id ? null : row.id)}
                                title="Bestand nach Lagerorten"
                              >
                                {expanded === row.id ? '▾' : '▸'} {row.name}
                              </button>
                            </td>
                            <td className="muted">{row.categoryName ?? <span className="faint">–</span>}</td>
                            <td className="muted">{row.supplierName ?? ''}</td>
                            <td
                              className="numeric"
                              style={below ? { color: 'var(--warning)', fontWeight: 600 } : undefined}
                            >
                              {formatQuantity(row.stockQuantity)}
                            </td>
                            <td className="numeric muted">
                              {row.reservedQuantity > 0 ? formatQuantity(row.reservedQuantity) : '—'}
                            </td>
                            <td
                              className="numeric"
                              style={row.freeQuantity < 0 ? { color: 'var(--danger)', fontWeight: 600 } : undefined}
                              title="Bestand minus reserviert"
                            >
                              {formatQuantity(row.freeQuantity)}
                            </td>
                            <td className="numeric muted">
                              {row.minStock > 0 ? formatQuantity(row.minStock) : '—'}
                            </td>
                            <td className="numeric muted">{formatPrice(row.averageCost)}</td>
                            <td className="numeric">{formatAmount(value)}</td>
                            <td className="numeric" title={outflowTitle(row)}>
                              {row.turnover !== null ? formatTurnover(row.turnover) : <span className="faint">—</span>}
                            </td>
                            <td className="numeric muted" title={outflowTitle(row)}>
                              {row.stockDays !== null ? formatDays(row.stockDays) : <span className="faint">—</span>}
                            </td>
                            <td className="numeric muted" title={outflowTitle(row)}>
                              {row.coverageDays !== null ? formatDays(row.coverageDays) : <span className="faint">—</span>}
                            </td>
                            {can('stock:write') ? (
                              <td>
                                <div className="row" style={{ gap: 6 }}>
                                  <button
                                    type="button"
                                    className="btn btn-sm"
                                    onClick={() => setAdjustItem(row)}
                                  >
                                    Korrigieren
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm"
                                    onClick={() => setTransferItem(row)}
                                  >
                                    Umlagern
                                  </button>
                                </div>
                              </td>
                            ) : null}
                          </tr>

                          {expanded === row.id ? (
                            <tr>
                              <td colSpan={can('stock:write') ? 14 : 13} style={{ background: 'var(--bg-sunken)' }}>
                                <WarehouseBreakdown itemId={row.id} />
                              </td>
                            </tr>
                          ) : null}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          ) : null}
        </div>
      ) : (
        <div className="card">
          {movements.isLoading ? <LoadingBlock /> : null}
          {movements.data ? (
            movements.data.items.length === 0 ? (
              <EmptyState title="Noch keine Lagerbewegungen" />
            ) : (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th className="shrink">Datum</th>
                      <th className="shrink">Vorgang</th>
                      <th>Artikel</th>
                      <th className="shrink">Beleg</th>
                      <th className="numeric">Menge</th>
                      <th className="numeric">Bestand danach</th>
                      <th className="numeric">Einstand</th>
                      <th>Notiz</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movements.data.items.map((movement) => (
                      <tr key={movement.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDate(movement.movementDate)}</td>
                        <td>{MOVEMENT_LABELS[movement.movementType] ?? movement.movementType}</td>
                        <td>
                          <span className="mono faint">{movement.itemNumber}</span> {movement.itemName}
                        </td>
                        <td className="mono">{movement.documentNumber ?? ''}</td>
                        <td
                          className="numeric"
                          style={{ color: movement.quantity < 0 ? 'var(--danger)' : 'var(--success)' }}
                        >
                          {movement.quantity > 0 ? '+' : ''}
                          {formatQuantity(movement.quantity)}
                        </td>
                        <td className="numeric">{formatQuantity(movement.balanceAfter)}</td>
                        <td className="numeric muted">{formatPrice(movement.unitCost)}</td>
                        <td className="muted">{movement.note ?? ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : null}
        </div>
      )}

      {adjustItem ? (
        <AdjustDialog item={adjustItem} onClose={() => setAdjustItem(null)} />
      ) : null}

      {transferItem ? (
        <TransferDialog item={transferItem} onClose={() => setTransferItem(null)} />
      ) : null}
    </>
  );
}

/**
 * Hinweis auf Bestände, die keine Bewegung hinter sich haben.
 *
 * Das passiert bei einem Import oder einem von Hand gesetzten
 * Anfangsbestand. Der Bestand ist dann zwar richtig, aber nicht
 * nachvollziehbar — und genau das verlangen die GoBD.
 */
function ReconcileBanner() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [reason, setReason] = useState('Anfangsbestand nachgetragen');

  const { data } = useQuery({
    queryKey: ['stock-reconcile'],
    queryFn: () =>
      api.get<{ count: number; differences: { number: string; name: string; difference: number }[] }>(
        '/api/stock/reconcile',
      ),
  });

  const reconcile = useMutation({
    mutationFn: () => api.post<{ count: number }>('/api/stock/reconcile', { reason }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['stock'] });
      queryClient.invalidateQueries({ queryKey: ['stock-reconcile'] });
      queryClient.invalidateQueries({ queryKey: ['stock-movements'] });
      toast.success(`${result.count} Artikel abgeglichen.`);
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Der Abgleich ist fehlgeschlagen.'),
  });

  if (!data || data.count === 0) return null;

  return (
    <Alert kind="warning">
      <div style={{ width: '100%' }}>
        <strong>
          Bei {data.count} {data.count === 1 ? 'Artikel' : 'Artikeln'} fehlt die Bewegung zum
          Bestand.
        </strong>
        <div style={{ marginTop: 4 }}>
          {data.differences
            .slice(0, 5)
            .map((entry) => `${entry.number} (${formatQuantity(entry.difference)})`)
            .join(', ')}
          {data.differences.length > 5 ? ' …' : ''}
        </div>
        <div className="row" style={{ gap: 8, marginTop: 10 }}>
          <TextInput value={reason} onChange={setReason} />
          <button
            type="button"
            className="btn btn-sm"
            disabled={reconcile.isPending || !reason.trim()}
            onClick={() => reconcile.mutate()}
          >
            Als Anfangsbestand buchen
          </button>
        </div>
        <div className="faint" style={{ fontSize: 12, marginTop: 6 }}>
          Der Bestand bleibt unverändert — es wird nur die fehlende Bewegung im Vorgabelager
          nachgetragen, damit die Historie lückenlos ist.
        </div>
      </div>
    </Alert>
  );
}

/** Bestand eines Artikels nach Lagerorten. */
function WarehouseBreakdown({ itemId }: { itemId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['stock-by-warehouse', itemId],
    queryFn: () =>
      api.get<{
        balances: WarehouseBalance[];
        total: number;
        itemQuantity: number;
        consistent: boolean;
      }>(`/api/stock/items/${itemId}/warehouses`),
  });

  if (isLoading) return <span className="muted">Bestände werden geladen …</span>;
  if (!data) return null;

  if (data.balances.length === 0) {
    return <span className="faint">Für diesen Artikel gibt es noch keine Lagerbewegungen.</span>;
  }

  return (
    <div style={{ padding: '4px 0' }}>
      <div className="row" style={{ gap: 20, flexWrap: 'wrap' }}>
        {data.balances.map((balance) => (
          <span key={balance.warehouseId} style={{ fontSize: 13 }}>
            <span className="mono faint">{balance.code}</span> {balance.name}:{' '}
            <strong className="mono">{formatQuantity(balance.quantity)}</strong>
          </span>
        ))}
      </div>

      {!data.consistent ? (
        <div style={{ marginTop: 6 }}>
          <Alert kind="warning">
            Die Summe der Lagerbestände ({formatQuantity(data.total)}) weicht vom Artikelbestand
            ({formatQuantity(data.itemQuantity)}) ab. Maßgeblich sind die Bewegungen.
          </Alert>
        </div>
      ) : null}
    </div>
  );
}

/** Umlagerung zwischen zwei Lagerorten. */
function TransferDialog({ item, onClose }: { item: StockRow; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [fromWarehouseId, setFrom] = useState('');
  const [toWarehouseId, setTo] = useState('');
  const [quantity, setQuantity] = useState(0);
  const [reason, setReason] = useState('');
  const [movementDate, setMovementDate] = useState(today());
  const [error, setError] = useState<string | null>(null);

  const { data: warehouses } = useQuery({
    queryKey: ['warehouses'],
    queryFn: () =>
      api.get<{ warehouses: { id: string; code: string; name: string; active: boolean }[] }>(
        '/api/stock/warehouses',
      ),
  });

  const { data: balances } = useQuery({
    queryKey: ['stock-by-warehouse', item.id],
    queryFn: () =>
      api.get<{ balances: WarehouseBalance[] }>(`/api/stock/items/${item.id}/warehouses`),
  });

  const available =
    balances?.balances.find((balance) => balance.warehouseId === fromWarehouseId)?.quantity ?? 0;

  const transfer = useMutation({
    mutationFn: () =>
      api.post('/api/stock/transfer', {
        itemId: item.id,
        fromWarehouseId,
        toWarehouseId,
        quantity,
        movementDate,
        reason,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock'] });
      queryClient.invalidateQueries({ queryKey: ['stock-by-warehouse', item.id] });
      queryClient.invalidateQueries({ queryKey: ['stock-movements'] });
      toast.success('Die Ware wurde umgelagert.');
      onClose();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Die Umlagerung ist fehlgeschlagen.'),
  });

  const options = (warehouses?.warehouses ?? [])
    .filter((warehouse) => warehouse.active)
    .map((warehouse) => ({ value: warehouse.id, label: `${warehouse.code} · ${warehouse.name}` }));

  return (
    <Dialog
      title={`Umlagern · ${item.number}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={
              transfer.isPending ||
              !fromWarehouseId ||
              !toWarehouseId ||
              fromWarehouseId === toWarehouseId ||
              quantity <= 0 ||
              !reason.trim()
            }
            onClick={() => {
              setError(null);
              transfer.mutate();
            }}
          >
            Umlagern
          </button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}

      <p className="muted" style={{ marginTop: 0 }}>
        {item.name} — der Gesamtbestand ändert sich dabei nicht, nur die Verteilung auf die
        Lagerorte.
      </p>

      <div className="form-grid">
        <Field label="Von Lagerort" span={6} required>
          <Select
            value={fromWarehouseId}
            onChange={(value) => {
              setFrom(value);
              setQuantity(0);
            }}
            placeholder="— bitte wählen —"
            options={options}
          />
        </Field>
        <Field label="Nach Lagerort" span={6} required>
          <Select
            value={toWarehouseId}
            onChange={setTo}
            placeholder="— bitte wählen —"
            options={options.filter((option) => option.value !== fromWarehouseId)}
          />
        </Field>

        <Field
          label="Menge"
          span={6}
          required
          hint={fromWarehouseId ? `Dort verfügbar: ${formatQuantity(available)}` : undefined}
        >
          <NumericInput kind="quantity" value={quantity} onChange={setQuantity} />
        </Field>
        <Field label="Datum" span={6}>
          <TextInput type="date" value={movementDate} onChange={setMovementDate} />
        </Field>

        <Field label="Grund" span={12} required>
          <TextArea
            value={reason}
            onChange={setReason}
            rows={2}
            placeholder="z. B. Kommissionierung für Auftrag AB-2026-0012"
          />
        </Field>
      </div>

      {fromWarehouseId && quantity > available ? (
        <Alert kind="error">
          Im abgebenden Lager liegen nur {formatQuantity(available)} Einheiten.
        </Alert>
      ) : null}
    </Dialog>
  );
}

/** Bestandskorrektur bzw. Inventurerfassung. */
function AdjustDialog({ item, onClose }: { item: StockRow; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [mode, setMode] = useState<'absolute' | 'relative'>('absolute');
  const [movementType, setMovementType] = useState<'inventory' | 'correction' | 'scrap'>('inventory');
  const [quantity, setQuantity] = useState(item.stockQuantity);
  const [reason, setReason] = useState('');
  const [movementDate, setMovementDate] = useState(today());
  const [errors, setErrors] = useState<Record<string, string>>({});

  const adjust = useMutation({
    mutationFn: () =>
      api.post<{ changed: boolean; delta?: number; message?: string }>('/api/stock/adjust', {
        itemId: item.id,
        mode,
        quantity,
        movementType,
        movementDate,
        reason,
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['stock'] });
      queryClient.invalidateQueries({ queryKey: ['stock-movements'] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
      toast.success(
        result.changed
          ? `Bestand korrigiert (${result.delta! > 0 ? '+' : ''}${formatQuantity(result.delta!)}).`
          : (result.message ?? 'Keine Änderung nötig.'),
      );
      onClose();
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setErrors(error.fieldErrors);
        toast.error(error.message);
      } else {
        toast.error('Die Korrektur ist fehlgeschlagen.');
      }
    },
  });

  const delta = mode === 'absolute' ? quantity - item.stockQuantity : quantity;

  return (
    <Dialog
      title={`Bestand korrigieren · ${item.number}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={adjust.isPending || !reason.trim()}
            onClick={() => {
              setErrors({});
              adjust.mutate();
            }}
          >
            Buchen
          </button>
        </>
      }
    >
      <Alert kind="info">
        Der Bestand wird nicht überschrieben, sondern durch eine nachvollziehbare Buchung
        verändert — die Differenz bleibt im Lagerjournal sichtbar.
      </Alert>

      <div className="stack" style={{ gap: 14 }}>
        <div className="definition-list">
          <dt>Artikel</dt>
          <dd>{item.name}</dd>
          <dt>Gebuchter Bestand</dt>
          <dd className="mono">{formatQuantity(item.stockQuantity)}</dd>
        </div>

        <Field label="Art der Buchung">
          <Select
            value={movementType}
            onChange={(value) => setMovementType(value)}
            options={[
              { value: 'inventory', label: 'Inventur (gezählter Bestand)' },
              { value: 'correction', label: 'Korrektur' },
              { value: 'scrap', label: 'Ausbuchung (Bruch, Schwund)' },
            ]}
          />
        </Field>

        <Field label="Eingabe">
          <Select
            value={mode}
            onChange={(value) => {
              setMode(value);
              setQuantity(value === 'absolute' ? item.stockQuantity : 0);
            }}
            options={[
              { value: 'absolute', label: 'Neuer Bestand insgesamt' },
              { value: 'relative', label: 'Zu- oder Abgang' },
            ]}
          />
        </Field>

        <Field
          label={mode === 'absolute' ? 'Gezählter Bestand' : 'Menge (negativ für Abgang)'}
          error={errors.quantity}
        >
          <NumericInput kind="quantity" value={quantity} onChange={setQuantity} allowNegative />
        </Field>

        <Field label="Buchungsdatum">
          <TextInput type="date" value={movementDate} onChange={setMovementDate} />
        </Field>

        <Field label="Begründung" required error={errors.reason}>
          <TextArea
            value={reason}
            onChange={setReason}
            rows={2}
            placeholder="z. B. Inventur 2026, Zählliste 14"
          />
        </Field>

        {delta !== 0 ? (
          <Alert kind={delta < 0 ? 'warning' : 'info'}>
            Es wird eine Buchung über {delta > 0 ? '+' : ''}
            {formatQuantity(delta)} erzeugt. Neuer Bestand:{' '}
            {formatQuantity(item.stockQuantity + delta)}.
          </Alert>
        ) : null}
      </div>
    </Dialog>
  );
}
